import React, { useState, useEffect } from 'react';
import { useReviewerAuth, ReviewerAPI } from '../context/ReviewerAuthContext';
import { useToast } from '../context/ToastContext';
import { ShieldCheck, Lock } from 'lucide-react';

const Spinner = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
    <div style={{ width: 28, height: 28, border: '3px solid var(--border)', borderTopColor: 'var(--pink)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
  </div>
);

// Wraps reviewer-only routes: fetches the CURRENT agreement version and
// compares it against what this reviewer has actually accepted (not just
// whether they've accepted *something*) — otherwise a reviewer who accepted
// an older version would slip past the UI gate and only get silently
// blocked server-side (403 from requireNdaAccepted) with no explanation.
export function ReviewerNdaBoundary({ reviewer, children }) {
  const [status, setStatus] = useState('checking'); // checking | ok | needs_accept

  useEffect(() => {
    let cancelled = false;
    ReviewerAPI.get('/api/reviewer/nda').then(r => {
      if (cancelled) return;
      const upToDate = reviewer.nda_accepted_at && reviewer.nda_agreement_version === r.data.version;
      setStatus(upToDate ? 'ok' : 'needs_accept');
    }).catch(() => { if (!cancelled) setStatus('needs_accept'); });
    return () => { cancelled = true; };
  }, [reviewer.nda_accepted_at, reviewer.nda_agreement_version]);

  if (status === 'checking') return <Spinner />;
  if (status === 'needs_accept') return <ReviewerNdaGate />;
  return children;
}

// Shown instead of the reviewer dashboard/review page until the reviewer has
// accepted the current confidentiality agreement version. Gated server-side
// too (requireNdaAccepted middleware) — this is the UI half of that gate.
function ReviewerNdaGate() {
  const { reviewer, acceptNda, logout } = useReviewerAuth();
  const { showToast } = useToast();
  const [agreement, setAgreement] = useState(null);
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    ReviewerAPI.get('/api/reviewer/nda').then(r => setAgreement(r.data)).catch(() => {});
  }, []);

  const handleAccept = async () => {
    setSubmitting(true);
    try {
      await acceptNda();
    } catch (err) {
      showToast(err.response?.data?.error || 'Could not record acceptance. Try again.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="card fade-up" style={{ maxWidth: 600, width: '100%', padding: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <ShieldCheck size={22} color="var(--pink)" />
          <h1 style={{ fontWeight: 900, fontSize: 20, color: 'var(--text)', letterSpacing: '-0.01em' }}>Confidentiality Agreement</h1>
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 18, fontWeight: 400 }}>
          Hi {reviewer?.name?.split(' ')[0]} — before you can view any assigned film, please read and accept this agreement.
        </p>

        <div style={{
          background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)',
          padding: '16px 18px', maxHeight: 320, overflowY: 'auto', fontSize: 12.5, lineHeight: 1.7,
          color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', fontWeight: 400, marginBottom: 18,
        }}>
          {agreement ? agreement.text : 'Loading agreement…'}
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, cursor: 'pointer' }}>
          <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 3 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text)', fontWeight: 500 }}>
            I have read and agree to this confidentiality agreement, and understand that my access to each film is logged.
          </span>
        </label>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={logout} className="btn-ghost" style={{ flex: 1 }}>Sign Out</button>
          <button onClick={handleAccept} disabled={!checked || !agreement || submitting} className="btn-primary" style={{ flex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <Lock size={13} /> {submitting ? 'Recording...' : 'I Agree & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
